/**
 * useAnalyticsAccounts Hook
 * 
 * Fetches accounts for Analytics page.
 * Handles loading state and error handling.
 */

import { useEffect, useState } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  QuerySnapshot,
  Unsubscribe,
  FirestoreError,
  DocumentSnapshot,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { Account } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

export interface UseAnalyticsAccountsParams {
  workspaceId: string;
}

export interface UseAnalyticsAccountsResult {
  accounts: Account[];
  setAccounts: React.Dispatch<React.SetStateAction<Account[]>>;
}

// Cache for accounts per workspace - persists across component unmounts
const accountsCache = new Map<string, {
  accounts: Account[];
  timestamp: number;
}>();

// Cache expiration time: 5 minutes
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook for fetching accounts for Analytics
 * Uses caching to avoid reloading data when navigating back to Analytics
 */
export const useAnalyticsAccounts = ({
  workspaceId,
}: UseAnalyticsAccountsParams): UseAnalyticsAccountsResult => {
  // Check cache first
  const cachedData = accountsCache.get(workspaceId);
  const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);
  
  const [accounts, setAccounts] = useState<Account[]>(
    hasValidCache && cachedData ? cachedData.accounts : []
  );

  useEffect(() => {
    // Allow loading in demo mode or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) return;

    const accountsRef = collection(db, "workspaces", workspaceId, "accounts");
    const q = query(accountsRef, orderBy("accountName", "asc"));

    const handleSnapshot = (snapshot: QuerySnapshot): void => {
      const accountsList: Account[] = snapshot.docs.map((docSnap: DocumentSnapshot) => {
        const data = docSnap.data();
        if (!data) {
          throw new Error("Account data is missing");
        }
        return {
          id: docSnap.id,
          userId: data.userId as string,
          accountName: data.accountName as string,
          group: data.group as import("../../../types").AccountGroup,
          amount: data.amount as number,
          currency: data.currency as string,
          description: data.description as string,
          createdAt: data.createdAt,
        };
      });
      
      // Update cache
      accountsCache.set(workspaceId, {
        accounts: accountsList,
        timestamp: Date.now(),
      });
      
      setAccounts(accountsList);
    };

    let unsubscribe: Unsubscribe | undefined;

    try {
      unsubscribe = onSnapshot(
        q,
        handleSnapshot,
        (error: FirestoreError) => {
          console.error("Error fetching accounts:", error);
        }
      );
    } catch (error) {
      console.error("Query setup error:", error);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [workspaceId]);

  return {
    accounts,
    setAccounts,
  };
};
